import { jsx as _jsx } from "react/jsx-runtime";
import MuiCircularProgress from '@mui/material/CircularProgress';
import * as React from 'react';
import { FlexBox } from '../box';
import { Chip } from '../chip';
import { useMounted } from '../hooks';
import { IconLocationOn } from '../icon';
import { Skeleton } from '../skeleton';
import { usePaletteColor } from '../theme';
import { Tooltip } from '../tooltip';
function buildTooltipContent(facilityInfo, moreText) {
    if (!facilityInfo) {
        return undefined;
    }
    const MAX_STUDIOS = 6;
    const { sharedFacilities = 0, whitelistEntries = [] } = facilityInfo;
    const facilities = whitelistEntries
        .filter(({ permission }) => permission !== 'NONE')
        .slice(0, MAX_STUDIOS)
        .map(({ facilityName, name }) => facilityName ?? name ?? '');
    const title = (() => {
        const list = [...facilities];
        const more = Math.max(0, sharedFacilities - facilities.length);
        if (more && moreText) {
            list.push(moreText(more));
        }
        return list.join('\n');
    })();
    return title;
}
const InternalFacilityBadge = (props) => {
    const [tooltipVisible, setTooltipVisibility] = React.useState(false);
    const colorText = usePaletteColor('components.facility.contrast.text');
    const colorIcon = usePaletteColor('components.facility.contrast.icon');
    const colorTextInactive = usePaletteColor('components.facility.contrast.text-inactive');
    const colorIconInactive = usePaletteColor('components.facility.contrast.icon-inactive');
    const bgHover = usePaletteColor('components.facility.background.hover');
    const bgDefault = usePaletteColor('components.facility.background.enabled');
    const bgInactive = usePaletteColor('components.facility.background.inactive');
    const onVisibilityChange = (visibility) => {
        setTooltipVisibility(visibility);
        props.onVisibilityChange?.(visibility);
    };
    const bgColor = props.inactive
        ? bgInactive
        : tooltipVisible
            ? `${bgHover} !important`
            : bgDefault;
    const textColor = props.inactive ? colorTextInactive : colorText;
    const iconColor = props.inactive ? colorIconInactive : colorIcon;
    return (_jsx(Tooltip, { title: props.tooltipContent, onVisibilityChange: onVisibilityChange, disabled: !props.tooltipContent, children: _jsx(Chip, { label: props.chipLabel, icon: _jsx(IconLocationOn, {}), sx: {
                color: textColor,
                bgcolor: bgColor,
                '.MuiChip-icon': {
                    color: iconColor
                }
            } }) }));
};
export const FacilityBadge = (props) => {
    const { moreText, facilityName, facilityInfo, loading, inactive } = props;
    if (loading) {
        return _jsx(Skeleton, { width: 42, height: 24 });
    }
    if (facilityName) {
        return (_jsx(InternalFacilityBadge, { chipLabel: facilityName, inactive: inactive }));
    }
    const { sharedFacilities = 0, whitelistEntries = [] } = facilityInfo ?? {};
    const tooltipContent = buildTooltipContent({ sharedFacilities, whitelistEntries }, moreText);
    return (_jsx(InternalFacilityBadge, { tooltipContent: tooltipContent, chipLabel: sharedFacilities, inactive: inactive }));
};
FacilityBadge.displayName = 'FacilityBadge';
const AsyncFacilityBadge = ({ label, moreText, onFetch, useCache = true }) => {
    const [facilityInfo, setFacilityInfo] = React.useState(undefined);
    const [isLoading, setIsLoading] = React.useState(false);
    const mounted = useMounted();
    const onVisibilityChange = (visible) => {
        if (facilityInfo && useCache) {
            return;
        }
        if (!isLoading && facilityInfo && useCache) {
            return;
        }
        if (!visible) {
            return;
        }
        setIsLoading(true);
        void (async () => {
            try {
                const info = await onFetch();
                if (!mounted.current) {
                    return;
                }
                setFacilityInfo(info);
            }
            finally {
                if (mounted.current) {
                    setIsLoading(false);
                }
            }
        })();
    };
    const tooltipContent = (() => {
        if (isLoading) {
            return (_jsx(FlexBox, { vAlign: "center", children: _jsx(MuiCircularProgress, { role: "loading", size: 16, sx: { color: 'inherit' } }) }));
        }
        if (facilityInfo === null) {
            return undefined;
        }
        return buildTooltipContent(facilityInfo, moreText);
    })();
    return (_jsx(InternalFacilityBadge, { tooltipContent: tooltipContent, chipLabel: label, onVisibilityChange: onVisibilityChange }));
};
AsyncFacilityBadge.displayName = 'FacilityBadge.Async';
FacilityBadge.Async = AsyncFacilityBadge;
export const SingleFacilityBadge = ({ facilityName }) => (_jsx(InternalFacilityBadge, { tooltipContent: facilityName }));
